/**
 * Shared "Moleskine notebook" page-turn flip — a single CSS 3D rotation of
 * the CURRENT page's own LIVE DOM (a real, already-rendered copy of the
 * page's markup — never a rasterized canvas snapshot, never anything built
 * asynchronously) over the NEXT/PREV page, which is already mounted
 * underneath, in normal flow, from the very first frame this module ever
 * touches. There is therefore never a frame with nothing (or a blank white
 * box) on screen — which is exactly what killed the old rasterized curl in
 * production: that version's curl "host" started life as an EMPTY element
 * sitting ABOVE the incoming page while `renderMangaPage()` did its async
 * work (await webfont readiness, `fetch()` each panel's image, decode,
 * `ctx.drawImage`, then `canvas.toDataURL()`), so any real-world delay in
 * that chain — slow network, a cold image cache, a webfont still loading —
 * painted a plain white rectangle over the manga for however long it took
 * (or forever, if the promise ever stalled; nothing watched it with a
 * timeout). Replacing that async raster with a synchronous CSS transform of
 * DOM that's already fully painted removes the failure mode at its root
 * instead of patching around it.
 *
 * Framework-agnostic ON PURPOSE — same convention the old curl used: this
 * module is used TWICE —
 *  1) imported and called directly by the React reader (manga-editor.tsx).
 *  2) `.toString()`-embedded verbatim into the exported standalone HTML
 *     reader's inline `<script>` (manga-export.ts, same convention as that
 *     file's own `readerRuntime`) — so `runPageFlip` must stay a single,
 *     self-contained function: no closures over outside module state, no
 *     imports/other-module references inside its body.
 */

export interface PageFlipOptions {
  /** Reading-advance direction — same convention the old curl used:
   *  1  → hinge (the "spine") sits at the LEFT edge (transform-origin 0%);
   *       the sheet rotates to a NEGATIVE angle, swinging open like a
   *       notebook cover read left-to-right (the "exit" edge when advancing
   *       forward is the RIGHT edge, which is where the sheet lifts from).
   * -1  → hinge at the RIGHT edge (100%); rotates POSITIVE — the mirror
   *       image, used for going backward / the RTL `navFlip` toggle. */
  dir: 1 | -1;
  /** Total animation duration, ms. Callers pass their own timing constant
   * explicitly (e.g. `READER_FLIP_MS`) so the two embeddings can never drift
   * — this default is only a safety net for a caller that forgets to. */
  durationMs?: number;
}

/**
 * Mounts the flip onto three already-laid-out elements (all must be mounted
 * and painted at their final on-screen box BEFORE this is called — the
 * caller is responsible for that, exactly like the old curl's `container`
 * contract):
 *
 *  - `sheet`: the rotating leaf. Must be `position: relative`, sized to the
 *    page's exact on-screen box (same box a normal page would occupy), and
 *    contain its own front face (the outgoing page's live content) and back
 *    face (plain paper) as `position: absolute; inset: 0;
 *    backface-visibility: hidden` children — see `.manga-flip-face*` in
 *    styles.css (and its 1:1 twin, READER_CSS, in manga-export.ts).
 *  - `incoming`: the next/prev page's own box, already mounted in normal
 *    flow UNDERNEATH `sheet` — receives a small "settle" scale pop as the
 *    flip lands, timed to land right as the sheet finishes rotating away.
 *  - `shade`: optional — a `position: absolute; inset: 0` overlay INSIDE the
 *    sheet's front face that darkens near the hinge as the sheet turns
 *    (sells the fold, like a real sheet catching the light edge-on). Pass
 *    `null` to skip it.
 *
 * Calls `onDone` exactly once — guaranteed even if the CSS `transitionend`
 * event never fires (backgrounded tab, a `transition` property clobbered
 * elsewhere, etc.) via a safety timeout slightly longer than the animation.
 * Returns `cancel()`, which tears down listeners/timers WITHOUT firing
 * `onDone` — for when the reader is closed/interrupted mid-flip (the DOM
 * nodes themselves belong to the caller, not this function, so there is
 * nothing else to remove).
 */
export function runPageFlip(
  sheet: HTMLElement,
  incoming: HTMLElement,
  shade: HTMLElement | null,
  opts: PageFlipOptions,
  onDone: () => void,
): () => void {
  const DURATION = opts.durationMs ?? 650;
  const hingeLeft = opts.dir === 1;
  const sign = hingeLeft ? -1 : 1;
  const ANGLE = 115; // degrees — shy of 180, reads as paper, not a vanishing card
  const EASE = 'cubic-bezier(0.45,0.05,0.35,1)';
  // The "settle" pop lands in the LAST slice of the turn — long enough to
  // read as a deliberate little bounce, never longer than the turn itself.
  const SETTLE_MS = Math.min(320, Math.round(DURATION * 0.45));
  const SETTLE_DELAY = Math.max(0, DURATION - SETTLE_MS);

  // ── Frame 0 — set synchronously, before this function returns, so the
  // very first paint the browser produces already matches this state (no
  // flash of an un-set/default style). ─────────────────────────────────────
  sheet.style.transformStyle = 'preserve-3d';
  sheet.style.transformOrigin = hingeLeft ? '0% 50%' : '100% 50%';
  sheet.style.transition = 'none';
  sheet.style.transform = 'rotateY(0deg)';
  sheet.style.boxShadow = '0 0 0 rgba(0,0,0,0)';
  sheet.style.borderRadius = '0px';

  incoming.style.transition = 'none';
  incoming.style.transform = 'scale(0.985)';

  if (shade) {
    shade.style.transition = 'none';
    shade.style.opacity = '0';
    // Darkest AT the hinge, fading out across the sheet — same "which edge"
    // logic as the transform-origin above.
    shade.style.background = hingeLeft
      ? 'linear-gradient(90deg, rgba(0,0,0,0.35), rgba(0,0,0,0) 45%)'
      : 'linear-gradient(270deg, rgba(0,0,0,0.35), rgba(0,0,0,0) 45%)';
  }

  // Force the browser to COMMIT the frame-0 state above as its own layout/
  // style pass before the transition (added below) has anything to animate
  // FROM — without this a browser may coalesce both writes into a single
  // paint and skip the animation outright.
  void sheet.offsetWidth;

  let done = false;
  let fallback: ReturnType<typeof setTimeout> | null = null;
  let raf = 0;

  function finish(): void {
    if (done) return;
    done = true;
    if (fallback) { clearTimeout(fallback); fallback = null; }
    sheet.removeEventListener('transitionend', onTransitionEnd);
    // Drop the flip's own transition timing from `incoming` (it stays in
    // normal flow afterward — the caller's NEXT transform change on it, e.g.
    // a guided-mode panel zoom, must use ITS OWN transition, not ours).
    incoming.style.transition = '';
    onDone();
  }

  function onTransitionEnd(e: TransitionEvent): void {
    if (e.target === sheet && e.propertyName === 'transform') finish();
  }
  sheet.addEventListener('transitionend', onTransitionEnd);

  raf = requestAnimationFrame(() => {
    if (done) return;
    sheet.style.transition = `transform ${DURATION}ms ${EASE}, box-shadow ${DURATION}ms ${EASE}, border-radius ${DURATION}ms ${EASE}`;
    sheet.style.transform = `rotateY(${sign * ANGLE}deg)`;
    // The shadow the lifting sheet casts falls toward the edge it's rotating
    // AWAY from — recedes together with the sheet since a CSS box-shadow is
    // computed against the element's own (3D-transformed) screen footprint.
    sheet.style.boxShadow = `${sign * -20}px 10px 50px rgba(0,0,0,0.28)`;
    sheet.style.borderRadius = '8px';

    incoming.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.34,1.56,0.64,1) ${SETTLE_DELAY}ms`;
    incoming.style.transform = 'scale(1)';

    if (shade) {
      shade.style.transition = `opacity ${DURATION}ms ${EASE}`;
      shade.style.opacity = '0.85';
    }
  });

  // Safety net — guarantees `onDone` always fires, so navigation can never
  // get stuck mid-turn even if `transitionend` is missed for any reason.
  fallback = setTimeout(finish, DURATION + 150);

  return () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    if (fallback) { clearTimeout(fallback); fallback = null; }
    sheet.removeEventListener('transitionend', onTransitionEnd);
  };
}
